import { MemberSocket } from "modules/api/websockets";

export interface LunaAttachment {
  s3_key: string;
  filename: string;
  content_type: string;
  size: number;
}

export interface LunaMessage {
  id: string;
  role: "assistant" | "user" | "tool" | "system" | "external";
  content: string | unknown[][]; // Can be string or nested array of blocks
  blocks?: UIBlock[];
  tool_calls?: ToolCall[];
  tool_executions?: ToolExecution[];
  tool_execution_id?: string; // Present on tool messages - response shown in tool call UI
  is_thinking: boolean;
  created_at: string;
  updated_at?: string;
  rating?: "good" | "bad" | null;
  status?: "pending" | "running" | "completed" | "failed";
  attachments?: LunaAttachment[];
  // Present only on a pending input's card (see LunaCardActions).
  actions?: LunaCardActions;
}

// A pending input's buttons, posted by Luna when an agent needs this person's
// go-ahead. Keys are camelCase on the wire, unlike the message envelope.
export interface LunaCardButton {
  actionId: string;
  label: string;
  kind: "confirm" | "reject" | "send";
  style: "primary" | "danger";
  // Present on Reject only: ask for a reason before sending.
  reasonPrompt?: boolean;
  reasonMaxLength?: number;
}

export interface LunaCardMenu {
  id: string;
  multi: boolean;
  placeholder?: string;
  options: { label: string; value: string }[];
}

export interface LunaCardActions {
  executionId: string;
  // "dashboard": answered only on the dashboard; the card never changes.
  state: "live" | "retired" | "dashboard";
  // Display text once retired. Rendered verbatim, never parsed.
  result: string | null;
  buttons: LunaCardButton[];
  menu: LunaCardMenu | null;
  links: { label: string; url: string }[];
}

// message:updated — the same card message, rewritten (answered, re-posted,
// expired, withdrawn or stopped).
export interface LunaMessageUpdate {
  id: string;
  content: string;
  actions: LunaCardActions;
}

export interface AnswerActionParams {
  actionId: string;
  reason?: string;
  selectedIds?: string[];
}

export type AnswerActionReply =
  | {
      status: "answered";
      outcome: "approved" | "rejected";
      executionId: string;
    }
  | { error: string; code: string };

export interface UIBlock {
  id?: string;
  type: string;
  content?: string;
  title?: string;
  description?: string;
  items?: unknown[];
  code?: string;
  language?: string;
  url?: string;
  label?: string;
  status?: string;
  rows?: unknown[][];
  headers?: string[];
  pairs?: { key: string; value: string }[];
  [key: string]: unknown;
}

export interface ToolCall {
  id: string;
  name: string;
  arguments: Record<string, unknown>;
  execution?: ToolExecution;
  blocks?: UIBlock[];
}

export interface ToolExecution {
  id: string;
  status:
    | "pending"
    | "running"
    | "completed"
    | "denied"
    | "confirmed"
    | "failed"
    | "pending_confirmation"
    | "pending_external";
  tool_name?: string;
  tool_label?: string;
  tool_call_id?: string;
  confirmation_message?: string;
  completion_message?: string;
  requires_confirmation?: boolean;
  call_blocks?: UIBlock[];
  response_blocks?: UIBlock[];
}

export interface LunaSessionUpdate {
  id: string;
  status: "idle" | "thinking" | "waiting_for_tool" | "complete" | "error";
  pending_tool_count?: number;
  title?: string;
}

export interface ArtifactUpdate {
  id: string;
  session_id: string;
  title: string;
  description: string | null;
  label: string | null;
  type: "luna_blocks" | "subagent";
  content: UIBlock[][] | Array<{ session_id: string }>;
}

type MessageHandler = (message: LunaMessage) => void;
type MessageUpdateHandler = (update: LunaMessageUpdate) => void;
type SessionHandler = (session: LunaSessionUpdate) => void;
type ToolHandler = (execution: ToolExecution & { sessionId: string }) => void;
type ArtifactHandler = (artifact: ArtifactUpdate) => void;

class LunaSocketService {
  private cleanupFns: Map<string, (() => void)[]> = new Map();
  private messageHandlers: Map<string, MessageHandler> = new Map();
  private messageUpdateHandlers: Map<string, MessageUpdateHandler> = new Map();
  private sessionHandlers: Map<string, SessionHandler> = new Map();
  private toolHandlers: Map<string, ToolHandler> = new Map();
  private artifactHandlers: Map<string, ArtifactHandler> = new Map();
  private connectedSessions: Set<string> = new Set();

  private getChannelName(sessionId: string): string {
    return `chat:${sessionId}`;
  }

  isConnected(sessionId: string): boolean {
    return this.connectedSessions.has(sessionId);
  }

  connect(sessionId: string): void {
    // Prevent duplicate connections
    if (this.connectedSessions.has(sessionId)) {
      return;
    }

    const channelName = this.getChannelName(sessionId);

    this.connectedSessions.add(sessionId);
    const cleanups: (() => void)[] = [];

    MemberSocket.addChannel(channelName);

    // Register event handlers
    const messageCleanup = MemberSocket.channelOn(
      channelName,
      "message:created",
      (message: LunaMessage) => {
        console.log("[Luna] message:created received:", message.id);
        const handler = this.messageHandlers.get(sessionId);
        if (handler) {
          console.log("[Luna] Calling message handler for session:", sessionId);
          handler(message);
        } else {
          console.warn("[Luna] No message handler for session:", sessionId);
        }
      },
    );
    cleanups.push(messageCleanup);

    const messageUpdateCleanup = MemberSocket.channelOn(
      channelName,
      "message:updated",
      (update: LunaMessageUpdate) => {
        console.log("[Luna] message:updated received:", update.id);
        this.messageUpdateHandlers.get(sessionId)?.(update);
      },
    );
    cleanups.push(messageUpdateCleanup);

    const sessionCleanup = MemberSocket.channelOn(
      channelName,
      "session:updated",
      (session: LunaSessionUpdate) => {
        console.log("[Luna] session:updated received:", session);
        this.sessionHandlers.get(sessionId)?.(session);
      },
    );
    cleanups.push(sessionCleanup);

    const toolCreatedCleanup = MemberSocket.channelOn(
      channelName,
      "tool_execution:created",
      (execution: ToolExecution & { is_thinking?: boolean }) => {
        console.log("[Luna] tool_execution:created received:", execution);
        this.toolHandlers.get(sessionId)?.({ ...execution, sessionId });
      },
    );
    cleanups.push(toolCreatedCleanup);

    const toolUpdatedCleanup = MemberSocket.channelOn(
      channelName,
      "tool_execution:updated",
      (execution: ToolExecution) => {
        console.log("[Luna] tool_execution:updated received:", execution);
        this.toolHandlers.get(sessionId)?.({ ...execution, sessionId });
      },
    );
    cleanups.push(toolUpdatedCleanup);

    const artifactUpdatedCleanup = MemberSocket.channelOn(
      channelName,
      "artifact:updated",
      (artifact: ArtifactUpdate) => {
        console.log("[Luna] artifact:updated received:", artifact.id);
        this.artifactHandlers.get(sessionId)?.(artifact);
      },
    );
    cleanups.push(artifactUpdatedCleanup);

    this.cleanupFns.set(sessionId, cleanups);
  }

  disconnect(sessionId: string): void {
    // Skip if not connected
    if (!this.connectedSessions.has(sessionId)) {
      return;
    }

    // Run cleanup functions
    const cleanups = this.cleanupFns.get(sessionId);
    cleanups?.forEach((fn) => fn());
    this.cleanupFns.delete(sessionId);

    // Remove handlers
    this.messageHandlers.delete(sessionId);
    this.messageUpdateHandlers.delete(sessionId);
    this.sessionHandlers.delete(sessionId);
    this.toolHandlers.delete(sessionId);
    this.artifactHandlers.delete(sessionId);

    // Mark as disconnected
    this.connectedSessions.delete(sessionId);

    // Remove channel
    const channelName = this.getChannelName(sessionId);
    MemberSocket.removeChannel(channelName);
  }

  onMessage(sessionId: string, handler: MessageHandler): void {
    this.messageHandlers.set(sessionId, handler);
  }

  onMessageUpdate(sessionId: string, handler: MessageUpdateHandler): void {
    this.messageUpdateHandlers.set(sessionId, handler);
  }

  onSessionUpdate(sessionId: string, handler: SessionHandler): void {
    this.sessionHandlers.set(sessionId, handler);
  }

  onToolUpdate(sessionId: string, handler: ToolHandler): void {
    this.toolHandlers.set(sessionId, handler);
  }

  onArtifactUpdate(sessionId: string, handler: ArtifactHandler): void {
    this.artifactHandlers.set(sessionId, handler);
  }

  async sendMessage(
    sessionId: string,
    content: string,
    attachments?: LunaAttachment[],
  ): Promise<void> {
    const channelName = this.getChannelName(sessionId);
    const payload: { content: string; attachments?: LunaAttachment[] } = {
      content,
    };
    if (attachments && attachments.length > 0) {
      payload.attachments = attachments;
    }
    // The channel always replies {:ok, …}; a failure (e.g. the 50 MB
    // conversation cap, or a missing/invalid S3 object) comes back as a clean
    // { error } string in the reply payload rather than an error push, so we
    // check for it explicitly and surface it to the caller.
    const reply = await MemberSocket.pushMessage<{
      status?: string;
      error?: string;
    }>(channelName, "send:message", payload);
    if (reply?.error) {
      throw new Error(reply.error);
    }
  }

  async confirmTool(sessionId: string, executionId: string): Promise<void> {
    const channelName = this.getChannelName(sessionId);
    await MemberSocket.pushMessage(channelName, "confirm:tool", {
      execution_id: executionId,
    });
  }

  async denyTool(
    sessionId: string,
    executionId: string,
    reason?: string,
  ): Promise<void> {
    const channelName = this.getChannelName(sessionId);
    await MemberSocket.pushMessage(channelName, "deny:tool", {
      execution_id: executionId,
      reason,
    });
  }

  // A click on a card's button. The channel always replies ok; a refusal comes
  // back as { error, code } in the payload. A channel error or timeout rejects.
  async answerAction(
    sessionId: string,
    params: AnswerActionParams,
  ): Promise<AnswerActionReply> {
    const channelName = this.getChannelName(sessionId);
    return MemberSocket.pushMessage<AnswerActionReply>(
      channelName,
      "action:answer",
      params,
    );
  }

  async getHistory(
    sessionId: string,
    limit = 50,
    offset = 0,
  ): Promise<{ messages: LunaMessage[]; is_thinking: boolean; has_more: boolean }> {
    const channelName = this.getChannelName(sessionId);
    const response = await MemberSocket.pushMessage<{
      messages: LunaMessage[];
      is_thinking?: boolean;
      has_more?: boolean;
    }>(channelName, "get:history", { limit, offset });
    return {
      messages: response?.messages || [],
      is_thinking: response?.is_thinking || false,
      has_more: response?.has_more || false,
    };
  }

  async getStatus(sessionId: string): Promise<LunaSessionUpdate | null> {
    const channelName = this.getChannelName(sessionId);
    const response = await MemberSocket.pushMessage<LunaSessionUpdate>(
      channelName,
      "get:status",
      {},
    );
    return response || null;
  }

  async getSession(sessionId: string): Promise<{
    id: string;
    title?: string;
    status: "idle" | "thinking" | "waiting_for_tool" | "complete" | "error" | "pending_external";
    pending_tool_count?: number;
    updated_at?: string;
  } | null> {
    const channelName = this.getChannelName(sessionId);
    const response = await MemberSocket.pushMessage<{
      id: string;
      title?: string;
      status: "idle" | "thinking" | "waiting_for_tool" | "complete" | "error" | "pending_external";
      pending_tool_count?: number;
      updated_at?: string;
    }>(channelName, "get:session", {});
    return response || null;
  }
}

// Singleton export
export const lunaSocket = new LunaSocketService();
